import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CellDataType } from '../interfaces/data-import/cell-data-type';
import { ICellDataTypeInfo } from '../interfaces/data-import/cell-data-type-info';
import { IColumnDefinition } from '../interfaces/data-import/column-definition';
import { IImportConfig } from '../interfaces/data-import/import-config';
import { ImportDataType } from '../interfaces/data-import/import-data-type';

/**
 * todo: check if this can be singleton
 */
@Injectable({ providedIn: 'root' })
export class DataImportService {
  private _imported$: EventEmitter<void> = new EventEmitter<void>();
  private _config: IImportConfig = null;

  constructor(private http: HttpClient) {}

  get imported$() {
    return this._imported$;
  }

  set config(value: IImportConfig) {
    this._config = value;
  }
  get config() {
    return this._config;
  }

  private dateFormats =
    'dd/MM/yyyy hh:mm:ss, dd/MM/yyyy hh:mm, dd/MM/yyyy HH:mm, dd/MM/yyyy HH:mm:ss, yyyy-MM-dd HH:mm:ss, yyyy-MM-dd HH:mm, dd/MM/yyyy';

  loadConfig(type: ImportDataType, isImpersonating: boolean) {
    if (type == ImportDataType.Drivers) {
      this.config = {
        firstRow: 0,
        maxErrorsCount: 10,
        columnDefinitions: this.getDriverColumnDefinitions(),
        type: ImportDataType.Drivers,
      };
    } else if (type == ImportDataType.Trucks) {
      this.config = {
        firstRow: 0,
        maxErrorsCount: 10,
        columnDefinitions: this.getTrucksColumnDefinitions(),
        type: ImportDataType.Trucks,
      };
    } else if (type == ImportDataType.Suppliers) {
      this.config = {
        firstRow: 0,
        maxErrorsCount: 10,
        columnDefinitions: this.getSuppliersColumnDefinitions(),
        type: ImportDataType.Suppliers,
      };
    } else if (type == ImportDataType.Orders) {
      this.config = {
        firstRow: 1,
        maxErrorsCount: null,
        columnDefinitions: isImpersonating
          ? this.getEngineiusOrderColumnDefinitions()
          : this.getCustomerOrderColumnDefinitions(),
        type: ImportDataType.Orders,
      };
    } else if (type == ImportDataType.NonMovementTransactions) {
      this.config = {
        firstRow: 1,
        maxErrorsCount: null,
        columnDefinitions: this.getNonMovementTransactionsColumnDefinitions(),
        type: ImportDataType.NonMovementTransactions,
      };
    }
  }

  private getDriverColumnDefinitions(): IColumnDefinition[] {
    return [
      {
        orderNumber: 1,
        targetFieldName: 'DrivingLicenceNumber',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 2,
        targetFieldName: 'FirstName',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 3,
        targetFieldName: 'MobileNumber',
        type: CellDataType.Phone,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 4,
        targetFieldName: 'NickName',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 5,
        targetFieldName: 'Surname',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 6,
        targetFieldName: 'DepotId',
        type: CellDataType.Number,
        isAllowNulls: false,
        typeInfo: {},
      },
    ];
  }

  private getTrucksColumnDefinitions(): IColumnDefinition[] {
    return [
      {
        orderNumber: 1,
        targetFieldName: 'DepotId',
        type: CellDataType.Number,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 2,
        targetFieldName: 'Capacity',
        type: CellDataType.Number,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 3,
        targetFieldName: 'Make',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 4,
        targetFieldName: 'Model',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 5,
        targetFieldName: 'AssetNumber',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 6,
        targetFieldName: 'Registration',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 7,
        targetFieldName: 'CapacityTowing',
        type: CellDataType.Number,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 8,
        targetFieldName: 'Description',
        type: CellDataType.String,
        isAllowNulls: true,
      },
      {
        orderNumber: 9,
        targetFieldName: 'TypeId',
        type: CellDataType.Number,
        isAllowNulls: false,
        typeInfo: {},
      },
    ];
  }

  private getSuppliersColumnDefinitions(): IColumnDefinition[] {
    return [
      {
        orderNumber: 1,
        targetFieldName: 'AddressLine1',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 2,
        targetFieldName: 'AddressLine2',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 3,
        targetFieldName: 'EmailAddress',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 4,
        targetFieldName: 'Name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 5,
        targetFieldName: 'Postcode',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 8 },
      },
      {
        orderNumber: 6,
        targetFieldName: 'Town',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 7,
        targetFieldName: 'Country',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 8,
        targetFieldName: 'CustomerId',
        type: CellDataType.Number,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 9,
        targetFieldName: 'PhoneNumber',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
    ];
  }

  private getCustomerOrderColumnDefinitions(): IColumnDefinition[] {
    return [
      {
        orderNumber: 1,
        targetFieldName: 'Customer_Name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 2,
        targetFieldName: 'Order_Type',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 3,
        targetFieldName: 'Account_Code',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 20 },
      },
      {
        orderNumber: 4,
        targetFieldName: 'Is_Vehicle_Taxed',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 5,
        targetFieldName: 'Is_MOT_Valid',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 8 },
      },
      {
        orderNumber: 6,
        targetFieldName: 'Customer_Order_Number',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 50 },
      },
      {
        orderNumber: 7,
        targetFieldName: 'User_Email_Address',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 191 },
      },
      {
        orderNumber: 8,
        targetFieldName: 'Vehicle_Reg',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 8 },
      },
      {
        orderNumber: 9,
        targetFieldName: 'VIN',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 17 },
      },
      {
        orderNumber: 10,
        targetFieldName: 'Make',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 30 },
      },
      {
        orderNumber: 11,
        targetFieldName: 'Model',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 12,
        targetFieldName: 'Kerb_Weight',
        type: CellDataType.Number,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 13,
        targetFieldName: 'Pick_Up_Business_Name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 14,
        targetFieldName: 'Pick_Up_Address_Line_1',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 15,
        targetFieldName: 'Pick_Up_Address_Line_2',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 16,
        targetFieldName: 'Pick_Up_Address_Line_3',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 17,
        targetFieldName: 'Pick_Up_Town',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 18,
        targetFieldName: 'Pick_Up_Postcode',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 19,
        targetFieldName: 'Pick_Up_Earliest',
        type: CellDataType.Date,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 20,
        targetFieldName: 'Pick_Up_Latest',
        type: CellDataType.Date,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 21,
        targetFieldName: 'Pick_Up_Additional_Requirements',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 22,
        targetFieldName: 'Pick_Up_Notes',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 23,
        targetFieldName: 'Drop_off_Business_Name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 24,
        targetFieldName: 'Drop_Off_Address_Line_1',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 25,
        targetFieldName: 'Drop_Off_Address_Line_2',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 26,
        targetFieldName: 'Drop_Off_Address_Line_3',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 27,
        targetFieldName: 'Drop_Off_Town',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 28,
        targetFieldName: 'Drop_Off_Postcode',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 29,
        targetFieldName: 'Drop_Off_Earliest',
        type: CellDataType.Date,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 30,
        targetFieldName: 'Drop_Off_Latest',
        type: CellDataType.Date,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 31,
        targetFieldName: 'Drop_Off_Additional_Requirements',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 32,
        targetFieldName: 'Drop_Off_Notes',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 33,
        targetFieldName: 'Held_End_Date',
        type: CellDataType.Date,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 34,
        targetFieldName: 'Purchase_Order_No',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 50 },
      },
      {
        orderNumber: 35,
        targetFieldName: 'Job_Tagging',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 36,
        targetFieldName: 'Department',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 37,
        targetFieldName: 'Team',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 38,
        targetFieldName: 'Pick_up_Forename',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 39,
        targetFieldName: 'Pick_up_Surname',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 40,
        targetFieldName: 'Pick_up_Email',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 41,
        targetFieldName: 'Pick_up_Phone1',
        type: CellDataType.Phone,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 42,
        targetFieldName: 'Pick_up_Phone2',
        type: CellDataType.Phone,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 43,
        targetFieldName: 'Pick_up_Notification',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 44,
        targetFieldName: 'Drop_off_Forename',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 45,
        targetFieldName: 'Drop_off_Surname',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 46,
        targetFieldName: 'Drop_off_Email',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 47,
        targetFieldName: 'Drop_off_Phone1',
        type: CellDataType.Phone,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 48,
        targetFieldName: 'Drop_off_Phone2',
        type: CellDataType.Phone,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 49,
        targetFieldName: 'Drop_off_Notification',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 50,
        targetFieldName: 'Step_Group_Name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 51,
        targetFieldName: 'Enforce_VRM_Validation',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 52,
        targetFieldName: 'Customer_Service_Fees',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 53,
        targetFieldName: 'pickup_customer_site_name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 54,
        targetFieldName: 'drop_off_customer_site_name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 55,
        targetFieldName: 'MoDel_Pin',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 56,
        targetFieldName: 'release_code',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 57,
        targetFieldName: 'fuel_type',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 58,
        targetFieldName: 'combine_group_code',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 10 },
      },
      {
        orderNumber: 59,
        targetFieldName: 'what_3_words_pick_up_address',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 200 },
      },
      {
        orderNumber: 60,
        targetFieldName: 'what_3_words_drop_off_address',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 200 },
      },
      {
        orderNumber: 61,
        targetFieldName: 'supplier',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 400 },
      },
    ];
  }

  private getEngineiusOrderColumnDefinitions(): IColumnDefinition[] {
    return [
      {
        orderNumber: 1,
        targetFieldName: 'Customer_Name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 2,
        targetFieldName: 'Order_Type',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 3,
        targetFieldName: 'Account_Code',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 20 },
      },
      {
        orderNumber: 4,
        targetFieldName: 'Is_Vehicle_Taxed',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 5,
        targetFieldName: 'Is_MOT_Valid',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 8 },
      },
      {
        orderNumber: 6,
        targetFieldName: 'Customer_Order_Number',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 50 },
      },
      {
        orderNumber: 7,
        targetFieldName: 'User_Email_Address',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 191 },
      },
      {
        orderNumber: 8,
        targetFieldName: 'Vehicle_Reg',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 8 },
      },
      {
        orderNumber: 9,
        targetFieldName: 'VIN',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 17 },
      },
      {
        orderNumber: 10,
        targetFieldName: 'Make',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 30 },
      },
      {
        orderNumber: 11,
        targetFieldName: 'Model',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 12,
        targetFieldName: 'Kerb_Weight',
        type: CellDataType.Number,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 13,
        targetFieldName: 'Pick_Up_Business_Name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 14,
        targetFieldName: 'Pick_Up_Address_Line_1',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 15,
        targetFieldName: 'Pick_Up_Address_Line_2',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 16,
        targetFieldName: 'Pick_Up_Address_Line_3',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 17,
        targetFieldName: 'Pick_Up_Town',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 18,
        targetFieldName: 'Pick_Up_Postcode',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 19,
        targetFieldName: 'Pick_Up_Earliest',
        type: CellDataType.Date,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 20,
        targetFieldName: 'Pick_Up_Latest',
        type: CellDataType.Date,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 21,
        targetFieldName: 'Pick_Up_Additional_Requirements',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 22,
        targetFieldName: 'Pick_Up_Notes',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 23,
        targetFieldName: 'Drop_off_Business_Name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 24,
        targetFieldName: 'Drop_Off_Address_Line_1',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 25,
        targetFieldName: 'Drop_Off_Address_Line_2',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 26,
        targetFieldName: 'Drop_Off_Address_Line_3',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 27,
        targetFieldName: 'Drop_Off_Town',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 28,
        targetFieldName: 'Drop_Off_Postcode',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 29,
        targetFieldName: 'Drop_Off_Earliest',
        type: CellDataType.Date,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 30,
        targetFieldName: 'Drop_Off_Latest',
        type: CellDataType.Date,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 31,
        targetFieldName: 'Drop_Off_Additional_Requirements',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 32,
        targetFieldName: 'Drop_Off_Notes',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 33,
        targetFieldName: 'Held_End_Date',
        type: CellDataType.Date,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 34,
        targetFieldName: 'Purchase_Order_No',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 50 },
      },
      {
        orderNumber: 35,
        targetFieldName: 'Job_Tagging',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 36,
        targetFieldName: 'Department',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 37,
        targetFieldName: 'Team',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 38,
        targetFieldName: 'Pick_up_Forename',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 39,
        targetFieldName: 'Pick_up_Surname',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 40,
        targetFieldName: 'Pick_up_Email',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 41,
        targetFieldName: 'Pick_up_Phone1',
        type: CellDataType.Phone,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 42,
        targetFieldName: 'Pick_up_Phone2',
        type: CellDataType.Phone,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 43,
        targetFieldName: 'Pick_up_Notification',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 44,
        targetFieldName: 'Drop_off_Forename',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 45,
        targetFieldName: 'Drop_off_Surname',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 46,
        targetFieldName: 'Drop_off_Email',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 47,
        targetFieldName: 'Drop_off_Phone1',
        type: CellDataType.Phone,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 48,
        targetFieldName: 'Drop_off_Phone2',
        type: CellDataType.Phone,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 49,
        targetFieldName: 'Drop_off_Notification',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 50,
        targetFieldName: 'Step_Group_Name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 51,
        targetFieldName: 'Enforce_VRM_Validation',
        type: CellDataType.Boolean,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 52,
        targetFieldName: 'Customer_Service_Fees',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 53,
        targetFieldName: 'pickup_customer_site_name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 54,
        targetFieldName: 'drop_off_customer_site_name',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 55,
        targetFieldName: 'MoDel_Pin',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 56,
        targetFieldName: 'release_code',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 57,
        targetFieldName: 'fuel_type',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 58,
        targetFieldName: 'combine_group_code',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 10 },
      },
      {
        orderNumber: 59,
        targetFieldName: 'what_3_words_pick_up_address',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 200 },
      },
      {
        orderNumber: 60,
        targetFieldName: 'what_3_words_drop_off_address',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 200 },
      },
      {
        orderNumber: 61,
        targetFieldName: 'supplier',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 400 },
      },
      {
        orderNumber: 62,
        targetFieldName: 'fixed_price',
        type: CellDataType.Number,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 63,
        targetFieldName: 'task_sequence',
        type: CellDataType.Number,
        isAllowNulls: true,
        typeInfo: <ICellDataTypeInfo>{ maxLength: 10 },
      },
      {
        orderNumber: 64,
        targetFieldName: 'Pick_up_Secondary_Email',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 65,
        targetFieldName: 'Drop_off_Secondary_Email',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
    ];
  }

  private getNonMovementTransactionsColumnDefinitions(): IColumnDefinition[] {
    return [
      {
        orderNumber: 1,
        targetFieldName: 'OrganisationName',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 2,
        targetFieldName: 'XeroCode',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 3,
        targetFieldName: 'TransactionLinePrefix',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 4,
        targetFieldName: 'CustomDescription',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
      {
        orderNumber: 5,
        targetFieldName: 'CreatedOn',
        type: CellDataType.Date,
        isAllowNulls: false,
        typeInfo: <ICellDataTypeInfo>{ dateFormats: this.dateFormats },
      },
      {
        orderNumber: 6,
        targetFieldName: 'Amount',
        type: CellDataType.String,
        isAllowNulls: false,
        typeInfo: {},
      },
      {
        orderNumber: 7,
        targetFieldName: 'DepartmentName',
        type: CellDataType.String,
        isAllowNulls: true,
        typeInfo: {},
      },
    ];
  }
}
